<template>
  <!--
    RAC-10027: the entire widget is gated on the demo_widget_enabled flag
    shared via HandleInertiaRequests. When the flag is off (default) the
    component renders nothing AND skips auto-start in onMounted, so all 18
    pages that import <DemoWidget> stop showing the tour without per-page
    changes. Flip the flag on once the tour is re-scripted to the current UI.
  -->
  <div v-if="demoEnabled">
    <!-- Demo Widget Trigger Button (Help Icon) -->
    <button
      v-if="!isVisible && showHelpIcon"
      @click="handleHelpClick"
      class="fixed bottom-6 right-6 z-40 bg-rent-risk-blue-400 hover:bg-black text-white rounded p-3 shadow-lg transition-all duration-300 hover:scale-110"
      title="Open Demo Guide"
    >
      <font-awesome-icon :icon="faQuestion" class="text-lg" />
    </button>

    <!-- Demo Widget Modal -->
    <Teleport to="body">
      <div v-if="isVisible" class="demo-widget-container fixed inset-0 z-[9997]">
        <!-- Single Overlay - either background or highlight -->
        <div 
          v-if="currentStep?.target"
          class="demo-highlight-overlay fixed inset-0 z-[9999]"
          :style="highlightStyle"
        ></div>
        <div 
          v-else
          class="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity duration-300"
        ></div>

        <!-- Demo Widget Modal -->
        <div class="fixed inset-0 z-[10000] pointer-events-none"
             :class="!currentStep?.target ? 'flex items-center justify-center p-4' : ''">
          <div 
            class="bg-white rounded-lg shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 pointer-events-auto"
            :class="currentStep?.target ? 'demo-widget-positioned' : ''"
            :style="modalPositionStyle"
          >
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200">
              <div class="flex items-center space-x-3">
                <div class="bg-blue-100 p-2 rounded-lg">
                  <font-awesome-icon :icon="faGraduationCap" class="text-blue-600 text-lg" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ currentStep?.title || 'Welcome!' }}</h3>
                  <p class="text-sm text-gray-500">
                    Step {{ currentStepIndex + 1 }} of {{ steps.length }}
                  </p>
                </div>
              </div>
              <button
                @click="closeDemo"
                class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <font-awesome-icon :icon="faTimes" class="text-lg" />
              </button>
            </div>

            <!-- Progress Bar -->
            <div class="px-6 pt-4">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div 
                  class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  :style="{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }"
                ></div>
              </div>
            </div>

            <!-- Content -->
            <div class="p-6">
              <div class="text-gray-700 leading-relaxed" v-html="currentStep?.content"></div>
              
              <!-- Step-specific actions or info -->
              <div v-if="currentStep?.info" class="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div class="flex items-start space-x-2">
                  <font-awesome-icon :icon="faInfoCircle" class="text-blue-600 mt-0.5" />
                  <div class="text-sm text-blue-800" v-html="currentStep.info"></div>
                </div>
              </div>
            </div>

            <!-- Navigation Footer -->
            <div class="flex items-center justify-between p-6 border-t border-gray-200">
              <button
                v-if="currentStepIndex > 0"
                @click="previousStep"
                class="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                <font-awesome-icon :icon="faArrowLeft" />
                <span>Previous</span>
              </button>
              <div v-else></div>

              <div class="flex space-x-3">
                <button
                  @click="skipDemo"
                  class="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors duration-200"
                >
                  Skip Tour
                </button>
                
                <button
                  v-if="currentStepIndex < steps.length - 1"
                  @click="nextStep"
                  class="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  <span>Next</span>
                  <font-awesome-icon :icon="faArrowRight" />
                </button>
                
                <button
                  v-else
                  @click="completeDemo"
                  class="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  <span>Complete</span>
                  <font-awesome-icon :icon="faCheck" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { appUrl } from '@/appUrl'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
/* import specific icons */
import {
    faArrowLeft,
    faArrowRight,
    faCheck,
    faGraduationCap,
    faInfoCircle,
    faQuestion,
    faTimes
} from '@fortawesome/pro-light-svg-icons'

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from 'axios'

export default {
  name: 'DemoWidget',
  components: {
    FontAwesomeIcon
  },
  props: {
    pageIdentifier: {
      type: String,
      required: true
    },
    steps: {
      type: Array,
      required: true
    },
    autoStart: {
      type: Boolean,
      default: true
    },
    showHelpIcon: {
      type: Boolean,
      default: true
    }
  },
  emits: ['demo-completed', 'demo-closed', 'step-changed'],
  setup(props, { emit }) {
    // RAC-10027: the feature flag shared via HandleInertiaRequests.
    // When false, the template's root <div v-if="demoEnabled"> renders
    // nothing, and onMounted skips auto-start. Backend /demo/* routes are
    // also not registered when the flag is off, so even if a stale cached
    // bundle slipped through, /demo/check-completion would 404.
    const page = usePage()
    const demoEnabled = computed(() => Boolean(page.props.demo_widget_enabled))

    const isVisible = ref(false)
    const currentStepIndex = ref(0)
    const isCompleted = ref(false)
    const highlightElement = ref(null)
    const currentSessionId = ref(null)
    const sessionStats = ref({})
    const highlightUpdateTrigger = ref(0) // Used to force highlight recalculation

    const currentStep = computed(() => props.steps[currentStepIndex.value])

    const highlightStyle = computed(() => {
      // Include the trigger in the dependency to force recalculation
      highlightUpdateTrigger.value
      
      if (!currentStep.value?.target || !highlightElement.value) return {}
      
      const rect = highlightElement.value.getBoundingClientRect()
      const padding = 8
      
      // Get viewport dimensions for proper clipping
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      
      // Calculate the cutout rectangle accounting for viewport coordinates
      const cutoutLeft = Math.max(0, rect.left - padding)
      const cutoutTop = Math.max(0, rect.top - padding)
      const cutoutRight = Math.min(viewportWidth, rect.right + padding)
      const cutoutBottom = Math.min(viewportHeight, rect.bottom + padding)
      
      return {
        clipPath: `polygon(
          0% 0%, 
          0% 100%, 
          ${cutoutLeft}px 100%, 
          ${cutoutLeft}px ${cutoutTop}px, 
          ${cutoutRight}px ${cutoutTop}px, 
          ${cutoutRight}px ${cutoutBottom}px, 
          ${cutoutLeft}px ${cutoutBottom}px, 
          ${cutoutLeft}px 100%, 
          100% 100%, 
          100% 0%
        )`
      }
    })

    const modalPositionStyle = computed(() => {
      // Include the trigger in the dependency to force recalculation
      highlightUpdateTrigger.value
      
      if (!currentStep.value?.target || !highlightElement.value) {
        // Center the modal when no target is specified
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        }
      }
      
      const rect = highlightElement.value.getBoundingClientRect()
      const modalWidth = 400
      const modalHeight = 400
      const padding = 20
      
      let left = rect.right + padding
      let top = Math.max(padding, rect.top - modalHeight / 2)
      
      // Adjust if modal would go off-screen
      if (left + modalWidth > window.innerWidth - padding) {
        left = rect.left - modalWidth - padding
      }
      
      if (top + modalHeight > window.innerHeight - padding) {
        top = window.innerHeight - modalHeight - padding
      }
      
      if (top < padding) {
        top = padding
      }

      // ---- NEW: Apply per-step offset ----
      const offset = currentStep.value.modalOffset || { x: 0, y: 0 }
      left += offset.x
      top += offset.y
      
      return {
        position: 'fixed',
        left: `${Math.max(padding, left)}px`,
        top: `${top}px`,
        transform: 'none',
        zIndex: 10001
      }
    })

    const checkCompletion = async () => {
      try {
        const response = await axios.get('/demo/check-completion', {
          params: {
            page_identifier: props.pageIdentifier
          }
        })
        
        const hasEverCompleted = response.data.has_ever_completed || false
        const shouldAutoStart = !hasEverCompleted
        
        console.log('DEBUG - checkCompletion results:', {
          hasEverCompleted,
          shouldAutoStart,
          pageIdentifier: props.pageIdentifier
        })
        
        isCompleted.value = hasEverCompleted
        
        return { hasEverCompleted, shouldAutoStart }
      } catch (error) {
        return { hasEverCompleted: false, shouldAutoStart: true }
      }
    }

    const startNewSession = async () => {
      try {
        
        const response = await axios.post('/demo/start-session', {
          page_identifier: props.pageIdentifier
        })
        
        currentSessionId.value = response.data.session_id
        return response.data
      } catch (error) {
        console.error('Error starting demo session:', error)
        console.error('Error details:', error.response?.data)
        // Don't throw - continue with demo even if session creation fails
        return null
      }
    }

    const updateStepProgress = async (stepIndex = null) => {
      if (!currentSessionId.value) {
        console.warn('No active session ID for step progress update')
        return
      }
      
      try {
        const currentIndex = stepIndex !== null ? stepIndex : currentStepIndex.value
       
        const response = await axios.post('/demo/update-step-progress', {
          page_identifier: props.pageIdentifier,
          session_id: currentSessionId.value,
          current_step: currentIndex,
          total_steps: props.steps.length
        })
        
        return response.data
      } catch (error) {
        throw error
      }
    }

    const markCompleted = async () => {
      if (!currentSessionId.value) {
        console.warn('No active session ID for completion')
        return
      }
      
      router.post(appUrl('/demo/mark-completed'),
        {
          page_identifier: props.pageIdentifier,
          session_id: currentSessionId.value
        },
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: (page) => {
            isCompleted.value = true
            currentSessionId.value = null // Clear session after completion
          },
          onError: (errors) => {
            console.error('Error marking demo as completed:', errors)
          }
        }
      )
    }

    const findTargetElement = (selector) => {
      if (!selector) {
        return null
      }
      
      // Handle different selector types
      if (selector.startsWith('#')) {
        const element = document.getElementById(selector.slice(1))
        return element
      } else if (selector.startsWith('.')) {
        const element = document.querySelector(selector)
        return element
      } else {
        // For data-demo-target selectors, find the visible one
        const elements = document.querySelectorAll(`[data-demo-target="${selector}"]`)
        if (elements.length === 0) {
          console.warn(`No elements found with data-demo-target="${selector}"`)
          return null
        }
        
        if (elements.length === 1) {
          return elements[0]
        }
        
        // If multiple elements, return the visible one
        for (let element of elements) {
          const style = window.getComputedStyle(element)
          if (style.display !== 'none' && style.visibility !== 'hidden' && element.offsetParent !== null) {
            return element
          }
        }
        return elements[0] // fallback to first if none are clearly visible
      }
    }

    const updateHighlight = async () => {
      
      // Remove previous highlight effects
      document.querySelectorAll('.demo-highlighted').forEach(el => {
        el.classList.remove('demo-highlighted')
      })
      
      if (currentStep.value?.target) {
        // console.log('Target found in step:', currentStep.value.target)
        await nextTick()
        highlightElement.value = findTargetElement(currentStep.value.target)
        
        if (highlightElement.value) {
          // Add highlight class to the element
          highlightElement.value.classList.add('demo-highlighted')
          // console.log('Element classes after adding highlight:', highlightElement.value.className)
          
          // Temporarily restore body scroll for scrollIntoView to work
          const originalOverflow = document.body.style.overflow
          document.body.style.overflow = ''
          
          // Scroll element into view
          highlightElement.value.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          })
          
          // Wait for scroll to complete, then restore overflow and update highlight
          setTimeout(() => {
            document.body.style.overflow = 'hidden'
            // Force highlight recalculation after scroll
            forceHighlightUpdate()
          }, 500) // Allow time for smooth scroll to complete
        }
      } else {
        highlightElement.value = null
      }
    }

    const handleHelpClick = () => {
      startDemo(true)
    }

    const startDemo = async (manualStart = false) => {     
      // If manually started (help button), always show
      // If auto-start, check completion status
      if (manualStart) {
        // console.log('Manual start - always showing demo')
      } else {
        const { hasEverCompleted, shouldAutoStart } = await checkCompletion()
        // console.log('Auto start check:', { hasEverCompleted, shouldAutoStart })

        if (!shouldAutoStart) {
          // console.log('Auto start disabled - not showing demo')
          return
        }
      }
      
      // Start a new session every time the demo is opened
      // console.log('About to start new session...')
      await startNewSession()
      // console.log('Session started, currentSessionId:', currentSessionId.value)

      currentStepIndex.value = 0 // Always start from beginning for new sessions
      isVisible.value = true
      // Prevent body scroll
      document.body.style.overflow = 'hidden'
      
      // Record the initial step visit (only if we have a session)
      if (currentSessionId.value) {
        // console.log('Recording initial step visit...')
        await updateStepProgress(0)
      } else {
        console.warn('No session ID available, skipping step tracking')
      }
      
      await updateHighlight()
    }

    const nextStep = async () => {
      if (currentStepIndex.value < props.steps.length - 1) {
        currentStepIndex.value++
        await updateHighlight()
        
        // Update step progress in database
        try {
          await updateStepProgress()
        } catch (error) {
          console.error('Failed to update step progress:', error)
        }
        
        emit('step-changed', currentStepIndex.value, currentStep.value)
      }
    }

    const previousStep = async () => {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value--
        await updateHighlight()
        
        // Update step progress in database (going backwards)
        try {
          await updateStepProgress()
        } catch (error) {
          console.error('Failed to update step progress:', error)
        }
        
        emit('step-changed', currentStepIndex.value, currentStep.value)
      }
    }

    const loadConfettiLibrary = () => {
      return new Promise((resolve) => {
        // Check if already loaded
        if (window.JSConfetti) {
          resolve()
          return
        }
        
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/js-confetti@latest/dist/js-confetti.browser.js'
        script.onload = () => resolve()
        script.onerror = () => resolve() // Resolve even on error to prevent blocking
        document.head.appendChild(script)
      })
    }
    
    const triggerConfetti = () => {
      if (!window.JSConfetti) return
      
      try {
        const jsConfetti = new JSConfetti()
        
        // First burst of confetti
        jsConfetti.addConfetti({
          confettiRadius: 6,
          confettiNumber: 300,
          confettiColors: [
            '#10b981', // green-500
            '#22c55e', // green-400
            '#86efac', // green-300
            '#3b82f6', // blue-500
            '#fbbf24', // yellow-400
            '#f59e0b', // amber-500
          ],
        })
        
        // Second burst with emojis after a short delay
        setTimeout(() => {
          jsConfetti.addConfetti({
            emojis: ['🎉', '✨', '🎊', '🏠', '🔑'],
            emojiSize: 60,
            confettiNumber: 30,
          })
        }, 200)
        
      } catch (error) {
        console.error('Confetti error:', error)
      }
    }

    const completeDemo = async () => {
      // Remove any highlight effects
      document.querySelectorAll('.demo-highlighted').forEach(el => {
        el.classList.remove('demo-highlighted')
      })
      
      await markCompleted()
      isVisible.value = false
      // Restore body scroll
      document.body.style.overflow = ''
      
      // Load confetti library and trigger confetti effect
      loadConfettiLibrary().then(() => {
        triggerConfetti()
      })
      
      emit('demo-completed')
    }

    const closeDemo = () => {
      // Remove any highlight effects
      document.querySelectorAll('.demo-highlighted').forEach(el => {
        el.classList.remove('demo-highlighted')
      })
      
      isVisible.value = false
      // Restore body scroll
      document.body.style.overflow = ''
      emit('demo-closed')
    }

    const skipDemo = async () => {
      // Remove any highlight effects
      document.querySelectorAll('.demo-highlighted').forEach(el => {
        el.classList.remove('demo-highlighted')
      })
      
      await markCompleted()
      isVisible.value = false
      // Restore body scroll
      document.body.style.overflow = ''
      emit('demo-completed')
    }

    // Function to force highlight recalculation
    const forceHighlightUpdate = () => {
      highlightUpdateTrigger.value++
    }

    // Throttle function to limit how often we update
    let updateTimeout = null
    const throttledUpdate = () => {
      if (updateTimeout) clearTimeout(updateTimeout)
      updateTimeout = setTimeout(() => {
        forceHighlightUpdate()
      }, 16) // ~60fps
    }

    onMounted(async () => {
      // RAC-10027: do nothing when the feature flag is off — no listeners,
      // no auto-start, no backend round-trip. Keeps the component a true
      // no-op when disabled.
      if (!demoEnabled.value) {
        return
      }

      // Add event listeners for window resize and scroll
      window.addEventListener('resize', throttledUpdate)
      window.addEventListener('scroll', throttledUpdate, true) // true for capture to handle all scroll events

      if (props.autoStart) {
        await startDemo(false) // false = auto start, not manual
      }
    })

    onUnmounted(() => {
      // Clean up event listeners
      window.removeEventListener('resize', throttledUpdate)
      window.removeEventListener('scroll', throttledUpdate, true)
      if (updateTimeout) clearTimeout(updateTimeout)
      
      // Clean up any remaining highlights and restore scroll
      document.querySelectorAll('.demo-highlighted').forEach(el => {
        el.classList.remove('demo-highlighted')
      })
      document.body.style.overflow = ''
    })

    return {
      demoEnabled,
      isVisible,
      currentStepIndex,
      currentStep,
      highlightStyle,
      modalPositionStyle,
      currentSessionId,
      sessionStats,
      handleHelpClick,
      updateStepProgress,
      startDemo,
      nextStep,
      previousStep,
      completeDemo,
      closeDemo,
      skipDemo,
      forceHighlightUpdate,
      faArrowLeft,
    faArrowRight,
    faCheck,
    faGraduationCap,
    faInfoCircle,
    faQuestion,
    faTimes
    }
  }
}
</script>